/**
 * Derives per-player status indicators from the current node state.
 * Returns a function (playerId) => { rightSlot, className } that layout
 * components pass to PlayerCard.
 */

import type { NodeState } from "@rivaled/shared";
import { motion } from "framer-motion";
import type { ReactNode } from "react";

export type PlayerStatus = {
  rightSlot: ReactNode;
  className?: string;
};

export type PlayerStatusFn = (playerId: string) => PlayerStatus;

function ReadyIcon() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 500, damping: 25 }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-brand-400">
        <path
          d="M5 13l4 4L19 7"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </motion.div>
  );
}

function WaitingSpinner() {
  return (
    <div
      className="w-4 h-4 rounded-full border-2 border-surface-500/50 border-t-surface-300/80 animate-spin"
      style={{ animationDuration: "1.2s" }}
    />
  );
}

function FailedIcon() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 500, damping: 25 }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-error-400">
        <path
          d="M18 6L6 18M6 6l12 12"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
        />
      </svg>
    </motion.div>
  );
}

function MicIcon() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 500, damping: 25 }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-brand-400">
        <path
          d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"
          fill="currentColor"
          opacity="0.3"
        />
        <path
          d="M19 10v2a7 7 0 01-14 0v-2"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path d="M12 19v4m-4 0h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </motion.div>
  );
}

const READY: PlayerStatus = { rightSlot: <ReadyIcon />, className: "bg-brand-500/[0.06]" };
const WAITING: PlayerStatus = { rightSlot: <WaitingSpinner /> };
const JOINED: PlayerStatus = { rightSlot: <MicIcon />, className: "bg-brand-500/[0.06]" };
const FAILED: PlayerStatus = { rightSlot: <FailedIcon />, className: "bg-error-500/[0.04]" };

export function getNodePlayerStatus(nodeState: NodeState | null): PlayerStatusFn | undefined {
  if (!nodeState) return undefined;

  const { nodeType, state } = nodeState;

  switch (nodeType) {
    case "ready_up": {
      const s = state as { readyPlayers: string[] };
      return (playerId) => (s.readyPlayers.includes(playerId) ? READY : WAITING);
    }

    case "voice_channel_join": {
      const s = state as { joinedPlayers: string[]; failedPlayers: string[] };
      return (playerId) => {
        if (s.joinedPlayers.includes(playerId)) return JOINED;
        if (s.failedPlayers?.includes(playerId)) return FAILED;
        return WAITING;
      };
    }

    case "lobby_created": {
      const s = state as { confirmedPlayers: string[] };
      return (playerId) => (s.confirmedPlayers.includes(playerId) ? READY : WAITING);
    }

    case "team_voice_split": {
      const s = state as { movedPlayers: string[]; failedPlayers: string[] };
      return (playerId) => {
        if (s.movedPlayers.includes(playerId)) return JOINED;
        if (s.failedPlayers?.includes(playerId)) return FAILED;
        return WAITING;
      };
    }

    case "character_pick": {
      const s = state as {
        characterPool: { id: string; name: string }[];
        pickedCharacters: Record<string, string>;
        pickOrder: string[];
        currentPickIndex: number;
      };
      const currentPickerId = s.pickOrder[s.currentPickIndex];
      return (playerId) => {
        const pickedCharId = s.pickedCharacters[playerId];
        if (pickedCharId) {
          const char = s.characterPool.find((c) => c.id === pickedCharId);
          return {
            rightSlot: (
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-brand-400/70 truncate max-w-[60px] hidden @sm:inline">
                  {char?.name ?? pickedCharId}
                </span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-brand-500/60 shrink-0"
                  aria-hidden="true"
                >
                  <path
                    d="M5 13l4 4L19 7"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            ),
            className: "bg-brand-500/[0.04]",
          };
        }
        if (playerId === currentPickerId) {
          return {
            rightSlot: (
              <div className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-pulse shrink-0" />
            ),
            className: "ring-1 ring-brand-500/30",
          };
        }
        return {
          rightSlot: <span className="text-[10px] text-surface-500 italic">Waiting</span>,
        };
      };
    }

    default:
      return undefined;
  }
}
